import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Global click-to-zoom: any <img data-zoom> opens fullscreen; same data-zoom-group = navigable gallery.
export const Lightbox = () => {
  const [state, setState] = useState(null);

  useEffect(() => {
    const onClick = (e) => {
      const img = e.target.closest?.("img[data-zoom]");
      if (!img) return;
      e.preventDefault();
      e.stopPropagation();
      const group = img.getAttribute("data-zoom-group");
      const nodes = group ? [...document.querySelectorAll(`img[data-zoom-group="${CSS.escape(group)}"]`)] : [img];
      const items = [];
      nodes.forEach((n) => { if (!items.some((it) => it.src === n.currentSrc || it.src === n.src)) items.push({ src: n.currentSrc || n.src, alt: n.alt }); });
      const index = Math.max(0, items.findIndex((it) => it.src === (img.currentSrc || img.src)));
      setState({ items, index });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  const close = useCallback(() => setState(null), []);
  const go = useCallback((d) => setState((s) => s && { ...s, index: (s.index + d + s.items.length) % s.items.length }), []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [state, close, go]);

  if (!state) return null;
  const { items, index } = state;
  const cur = items[index];
  const multi = items.length > 1;
  const navBtn = "absolute bottom-5 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 flex h-12 w-12 items-center justify-center border border-gold/40 bg-black/60 text-gold backdrop-blur-md transition-colors duration-300 hover:bg-gold hover:text-ink";

  return createPortal(
    <div data-testid="lightbox" role="dialog" aria-modal="true" onClick={close} className="lightbox-in fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl">
      <img
        key={cur.src}
        src={cur.src}
        alt={cur.alt}
        data-testid="lightbox-image"
        onClick={(e) => e.stopPropagation()}
        className="lightbox-img max-h-[74vh] max-w-[94vw] sm:max-h-[86vh] sm:max-w-[92vw] select-none object-contain shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]"
      />
      <button onClick={close} data-testid="lightbox-close" aria-label="Chiudi" className="absolute right-5 top-5 flex h-12 w-12 items-center justify-center border border-gold/40 bg-black/60 text-gold backdrop-blur-md transition-colors duration-300 hover:bg-gold hover:text-ink">
        <X size={20} strokeWidth={1.5} />
      </button>
      {multi && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="lightbox-prev" aria-label="Precedente" className={`${navBtn} left-4 sm:left-8`}>
            <ChevronLeft size={22} strokeWidth={1.5} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="lightbox-next" aria-label="Successiva" className={`${navBtn} right-4 sm:right-8`}>
            <ChevronRight size={22} strokeWidth={1.5} />
          </button>
          <span data-testid="lightbox-counter" className="absolute bottom-9 left-1/2 -translate-x-1/2 text-[11px] sm:bottom-6 uppercase tracking-widest text-gold/70">
            {index + 1} / {items.length}
          </span>
        </>
      )}
    </div>,
    document.body
  );
};
